/**
 * FBR / PRA.
 *
 * Two jobs on one screen:
 *
 *   Setup    - which authority this till reports to, its tokens, its POS ID,
 *              sandbox or production, and a button to prove the gateway can be
 *              reached before anyone relies on it.
 *
 *   The queue - what is waiting, what was rejected and why. This is the screen
 *              that catches the failure mode this product is designed around:
 *              a shop trading happily offline for three days with nothing
 *              reaching the authority.
 *
 * Tokens are never sent back to the browser. The form shows whether one is set
 * and leaves the field blank; saving without retyping keeps the stored value.
 */

import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
    FiSend, FiRefreshCw, FiCheckCircle, FiXCircle, FiWifiOff, FiPlay,
    FiKey, FiAlertTriangle, FiExternalLink, FiZap
} from 'react-icons/fi';
import toast from 'react-hot-toast';

import { fiscalApi } from '../services/endpoints';
import useAsync, { useAction } from '../hooks/useAsync';
import DataTable from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import { PageHeader, StatCard, StatusBadge, Badge } from '../components/ui/Feedback';
import { SpinnerBlock } from '../components/ui/Spinner';
import { Field, Input, Toggle } from '../components/ui/Field';
import { money, formatDateTime, relativeTime } from '../utils/format';
import { loadFiscalStatus } from '../store/uiSlice';

/* ----------------------------- setup for one authority -------------------- */

const AuthorityCard = ({ integration, isActive, onSave, onTest, onActivate, busy }) => {
    const [form, setForm] = useState({
        sandboxToken: '',
        productionToken: '',
        posId: integration.POS_ID || '',
        staticIpAddress: integration.Static_IP_Address || '',
        productionFlag: !!integration.Production_Flag,
        autoSubmitEnabled: integration.Auto_Submit_Enabled !== false
    });
    const [expanded, setExpanded] = useState(false);

    const isPra = integration.Payload_Format === 'PRA';
    const needsPosId = isPra && !integration.POS_ID;
    const noEndpoints = !integration.Sandbox_Base_URL && !integration.Production_Base_URL;

    return (
        <section className={`card overflow-hidden ${isActive ? 'ring-2 ring-brand-500' : ''}`}>
            <header
                className="flex items-center gap-2 border-b border-slate-200 p-2"
                style={integration.Theme_Primary_Color
                    ? { borderLeft: `4px solid ${integration.Theme_Primary_Color}` }
                    : undefined}
            >
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <h2 className="font-semibold text-slate-800">{integration.Authority_Name}</h2>
                        {isActive && <Badge tone="brand">Active</Badge>}
                        {!integration.Authority_Is_Active && <Badge tone="slate">Not configured</Badge>}
                        {integration.Production_Flag
                            ? <Badge tone="green">Production</Badge>
                            : <Badge tone="amber">Sandbox</Badge>}
                    </div>
                    <p className="mt-0.5 text-sm text-slate-500">{integration.Authority_Description}</p>
                </div>

                {integration.Authority_Portal_URL && (
                    <a
                        href={integration.Authority_Portal_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-ghost btn-sm shrink-0"
                    >
                        Portal <FiExternalLink className="h-3.5 w-3.5" />
                    </a>
                )}
            </header>

            <div className="space-y-3 p-4">
                {/* The two ways this authority is not ready, said plainly. */}
                {noEndpoints && (
                    <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                        <FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <div>
                            <p className="font-medium">No gateway URL is set.</p>
                            <p>
                                {isPra
                                    ? 'PRA issues a base URL per merchant at onboarding. Enter it in the database (REVENUE_AUTHORITY) or ask your integrator.'
                                    : 'Set the base URL for this authority before enabling it.'}
                            </p>
                        </div>
                    </div>
                )}

                {needsPosId && (
                    <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                        <FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <p>
                            PRA needs the POS ID from your onboarding letter. Invoices are rejected
                            without it.
                        </p>
                    </div>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                    <Field
                        label="Sandbox token"
                        hint={integration.Has_Sandbox_Token ? 'A token is saved. Leave blank to keep it.' : 'Not set.'}
                    >
                        <Input
                            type="password"
                            placeholder={integration.Has_Sandbox_Token ? '••••••••••••' : 'Paste the sandbox token'}
                            value={form.sandboxToken}
                            onChange={(event) => setForm({ ...form, sandboxToken: event.target.value })}
                        />
                    </Field>

                    <Field
                        label="Production token"
                        hint={integration.Has_Production_Token ? 'A token is saved. Leave blank to keep it.' : 'Not set.'}
                    >
                        <Input
                            type="password"
                            placeholder={integration.Has_Production_Token ? '••••••••••••' : 'Paste the production token'}
                            value={form.productionToken}
                            onChange={(event) => setForm({ ...form, productionToken: event.target.value })}
                        />
                    </Field>

                    <Field
                        label={isPra ? 'PRA POS ID' : 'POS ID'}
                        hint={isPra ? 'Printed on the receipt and sent as bposid.' : 'Optional.'}
                    >
                        <Input
                            value={form.posId}
                            onChange={(event) => setForm({ ...form, posId: event.target.value })}
                            placeholder="123456"
                        />
                    </Field>

                    <Field label="Registered IP" hint="The address the authority whitelisted.">
                        <Input
                            value={form.staticIpAddress}
                            onChange={(event) => setForm({ ...form, staticIpAddress: event.target.value })}
                            placeholder="203.0.113.10"
                        />
                    </Field>
                </div>

                <div className="space-y-3 rounded-lg bg-slate-50 p-3">
                    <Toggle
                        label="Production mode"
                        description="Off means sandbox. A sandbox invoice is marked on the receipt and is not a legal invoice."
                        checked={form.productionFlag}
                        onChange={(value) => setForm({ ...form, productionFlag: value })}
                    />
                    <Toggle
                        label="Submit automatically"
                        description="Off keeps selling but stops sending. The queue grows until it is switched back on."
                        checked={form.autoSubmitEnabled}
                        onChange={(value) => setForm({ ...form, autoSubmitEnabled: value })}
                    />
                </div>

                {expanded && integration.Token_Instructions && (
                    <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600 whitespace-pre-line">
                        {integration.Token_Instructions}
                    </div>
                )}

                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        className="btn-primary"
                        disabled={busy}
                        onClick={() => onSave(integration.Authority_ID, form)}
                    >
                        <FiKey className="h-4 w-4" /> Save settings
                    </button>

                    <button
                        type="button"
                        className="btn-secondary"
                        disabled={busy || noEndpoints}
                        onClick={() => onTest(integration.Authority_ID)}
                    >
                        <FiZap className="h-4 w-4" /> Test connection
                    </button>

                    {!isActive && integration.Authority_Is_Active && (
                        <button
                            type="button"
                            className="btn-secondary"
                            disabled={busy}
                            onClick={() => onActivate(integration.Authority_Code)}
                        >
                            Report to {integration.Authority_Short_Name}
                        </button>
                    )}

                    {integration.Token_Instructions && (
                        <button
                            type="button"
                            className="btn-ghost btn-sm ml-auto"
                            onClick={() => setExpanded((value) => !value)}
                        >
                            {expanded ? 'Hide' : 'Show'} instructions
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
};

/* --------------------------------- the page ------------------------------- */

const FiscalPage = () => {
    const dispatch = useDispatch();
    const [tab, setTab] = useState('queue');
    const [queueFilters, setQueueFilters] = useState({ status: '', page: 1, pageSize: 50 });
    const [detail, setDetail] = useState(null);

    const loadStatus = useCallback(() => fiscalApi.status(), []);
    const { data: status, loading: statusLoading, refetch: refetchStatus } = useAsync(loadStatus, []);

    const loadQueue = useCallback(() => fiscalApi.listQueue(queueFilters), [queueFilters]);
    const { data: queue, meta, loading: queueLoading, refetch: refetchQueue } = useAsync(loadQueue, [queueFilters]);

    const refreshAll = () => {
        refetchStatus();
        refetchQueue();
        dispatch(loadFiscalStatus());
    };

    const { run: save, busy: saving } = useAction(
        (authorityId, form) => fiscalApi.saveIntegration({ authorityId, ...form }),
        { successMessage: 'Settings saved.', onSuccess: refreshAll }
    );

    const { run: test } = useAction(async (authorityId) => {
        const result = await fiscalApi.testConnection(authorityId);
        if (result.ok) toast.success(result.message, { duration: 7000 });
        else toast.error(result.message, { duration: 8000 });
        return result;
    }, { toastError: true });

    const { run: activate } = useAction(
        (code) => fiscalApi.setActiveAuthority(code),
        { onSuccess: refreshAll }
    );

    const { run: setEnabled } = useAction(
        (enabled) => fiscalApi.setEnabled(enabled),
        { onSuccess: refreshAll }
    );

    const { run: retry } = useAction(
        (queueId) => fiscalApi.retry(queueId),
        { successMessage: 'Queued for another attempt.', onSuccess: refreshAll }
    );

    const { run: retryAll } = useAction(
        () => fiscalApi.retryAllFailed(),
        { onSuccess: refreshAll }
    );

    const { run: runWorker, busy: running } = useAction(
        () => fiscalApi.run(),
        { onSuccess: refreshAll }
    );

    if (statusLoading && !status) return <SpinnerBlock label="Loading fiscal settings…" />;

    const queueColumns = [
        {
            key: 'Order_Number',
            header: 'Order',
            render: (row) => (
                <div>
                    <p className="font-medium text-slate-800">{row.Order_Number}</p>
                    <p className="text-xs text-slate-500">{row.Invoice_Number || '—'}</p>
                </div>
            )
        },
        {
            key: 'Order_Date_Time',
            header: 'When',
            render: (row) => (
                <span className="text-slate-600">{formatDateTime(row.Order_Date_Time)}</span>
            )
        },
        {
            key: 'Total_Payable',
            header: 'Amount',
            align: 'right',
            render: (row) => money(row.Total_Payable)
        },
        {
            key: 'Authority_Short_Name',
            header: 'Authority',
            render: (row) => (
                <div className="flex items-center gap-1.5">
                    {row.Authority_Short_Name}
                    {row.Is_Production === false && <Badge tone="amber">sandbox</Badge>}
                </div>
            )
        },
        {
            key: 'Queue_Status',
            header: 'Status',
            render: (row) => (
                <div className="space-y-0.5">
                    <StatusBadge status={row.Queue_Status} />
                    {row.Attempt_Count > 0 && (
                        <p className="text-[10px] text-slate-500">
                            attempt {row.Attempt_Count} of {row.Max_Attempts}
                        </p>
                    )}
                </div>
            )
        },
        {
            key: 'Authority_Invoice_Number',
            header: 'Invoice number',
            render: (row) => (
                row.Authority_Invoice_Number
                    ? <span className="tabular text-xs">{row.Authority_Invoice_Number}</span>
                    : <span className="text-xs text-slate-400">—</span>
            )
        },
        {
            key: 'Last_Error',
            header: 'Last result',
            render: (row) => (
                <span className="block max-w-[18rem] truncate text-xs text-slate-600" title={row.Last_Error}>
                    {row.Last_Error || (row.Queue_Status === 'DONE' ? 'Accepted' : 'Waiting')}
                </span>
            )
        },
        {
            key: 'Next_Attempt_Date_Time',
            header: 'Next try',
            render: (row) => (
                row.Queue_Status === 'PENDING'
                    ? <span className="text-xs text-slate-500">{relativeTime(row.Next_Attempt_Date_Time)}</span>
                    : <span className="text-xs text-slate-400">—</span>
            )
        },
        {
            key: 'actions',
            header: '',
            render: (row) => (
                <div className="flex justify-end gap-1">
                    <button
                        type="button"
                        className="btn-ghost btn-sm !px-2"
                        onClick={(event) => { event.stopPropagation(); setDetail(row); }}
                        title="Details"
                    >
                        <FiSend className="h-4 w-4" />
                    </button>
                    {row.Queue_Status !== 'DONE' && (
                        <button
                            type="button"
                            className="btn-ghost btn-sm !px-2 text-amber-600"
                            onClick={(event) => { event.stopPropagation(); retry(row.Fiscal_Queue_ID); }}
                            title="Try again now"
                        >
                            <FiRefreshCw className="h-4 w-4" />
                        </button>
                    )}
                </div>
            )
        }
    ];

    const activeCode = status?.activeAuthorityCode;

    return (
        <div className="page">
            <PageHeader
                title="FBR / PRA"
                subtitle="Digital invoicing - what has been reported and what is waiting"
                actions={(
                    <>
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => runWorker()}
                            disabled={running || !status?.enabled}
                        >
                            <FiPlay className="h-4 w-4" /> {running ? 'Sending…' : 'Send now'}
                        </button>
                        <button type="button" className="btn-secondary" onClick={refreshAll}>
                            <FiRefreshCw className="h-4 w-4" /> Refresh
                        </button>
                    </>
                )}
            />

            {/* The master switch, stated in terms of what it does to trading. */}
            <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border py-1 px-3
                             ${status?.enabled
                                ? 'border-emerald-200 bg-emerald-50'
                                : 'border-slate-200 bg-white'}`}>
                <div>
                    <p className="font-medium text-slate-800">
                        {status?.enabled
                            ? `Reporting to ${activeCode || 'the authority'}`
                            : 'Fiscal reporting is off'}
                    </p>
                    <p className="text-sm text-slate-600">
                        {status?.enabled
                            ? 'Every settled bill is queued and sent. Sales are never blocked if the gateway is unreachable.'
                            : 'Sales are recorded locally and printed, but nothing is sent to FBR or PRA.'}
                    </p>
                </div>

                <button
                    type="button"
                    className={status?.enabled ? 'btn-secondary' : 'btn-primary'}
                    onClick={() => setEnabled(!status?.enabled)}
                >
                    {status?.enabled ? 'Turn reporting off' : 'Turn reporting on'}
                </button>
            </div>

            {/* Queue health */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 xl:grid-cols-4">
                <StatCard
                    label="Waiting"
                    value={status?.queue?.pending ?? 0}
                    sub={status?.queue?.oldestPending
                        ? `oldest ${relativeTime(status.queue.oldestPending)}`
                        : 'nothing waiting'}
                    icon={FiWifiOff}
                    tone={status?.queue?.pending > 0 ? 'amber' : 'slate'}
                />
                <StatCard
                    label="Rejected"
                    value={status?.queue?.failed ?? 0}
                    sub={status?.queue?.failed > 0 ? 'needs a person' : 'none'}
                    icon={FiXCircle}
                    tone={status?.queue?.failed > 0 ? 'red' : 'slate'}
                />
                <StatCard
                    label="Accepted today"
                    value={status?.queue?.doneToday ?? 0}
                    icon={FiCheckCircle}
                    tone="green"
                />
                <StatCard
                    label="Worker"
                    value={status?.worker?.running ? 'Running' : 'Stopped'}
                    sub={status?.worker?.lastRunAt
                        ? `last pass ${relativeTime(status.worker.lastRunAt)}`
                        : 'not run yet'}
                    icon={FiPlay}
                    tone={status?.worker?.running ? 'green' : 'slate'}
                />
            </div>

            {/* Tabs */}
            <div className="flex gap-2 overflow-x-auto scrollbar-none border-b border-slate-200">
                {[
                    { key: 'queue', label: 'Submission queue' },
                    { key: 'setup', label: 'Setup' }
                ].map((item) => (
                    <button
                        key={item.key}
                        type="button"
                        onClick={() => setTab(item.key)}
                        className={`-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm
                                    font-medium transition-colors sm:px-4 lg:py-2
                                    ${tab === item.key
                                        ? 'border-brand-600 text-brand-800'
                                        : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            {tab === 'queue' ? (
                <>
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                        {['', 'PENDING', 'FAILED', 'DONE', 'CANCELLED'].map((value) => (
                            <button
                                key={value || 'all'}
                                type="button"
                                onClick={() => setQueueFilters({ ...queueFilters, status: value, page: 1 })}
                                className={queueFilters.status === value
                                    ? 'btn-primary btn-sm'
                                    : 'btn-secondary btn-sm'}
                            >
                                {value ? value.charAt(0) + value.slice(1).toLowerCase() : 'All'}
                            </button>
                        ))}

                        {status?.queue?.failed > 0 && (
                            <button type="button" className="btn-secondary btn-sm sm:ml-auto" onClick={() => retryAll()}>
                                <FiRefreshCw className="h-3.5 w-3.5" />
                                Retry all {status.queue.failed} rejected
                            </button>
                        )}
                    </div>

                    <DataTable
                        className="min-h-0 flex-1"
                        columns={queueColumns}
                        rows={queue || []}
                        loading={queueLoading}
                        meta={meta}
                        onPageChange={(page) => setQueueFilters({ ...queueFilters, page })}
                        rowKey={(row) => row.Fiscal_Queue_ID}
                        onRowClick={setDetail}
                        emptyTitle="The queue is empty"
                        emptyDescription="Settled bills appear here until the authority accepts them."
                        emptyIcon={FiCheckCircle}
                    />
                </>
            ) : (
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
                    {(status?.integrations || []).map((integration) => (
                        <AuthorityCard
                            key={integration.Authority_ID}
                            integration={integration}
                            isActive={integration.Authority_Code === activeCode}
                            onSave={save}
                            onTest={test}
                            onActivate={activate}
                            busy={saving}
                        />
                    ))}
                </div>
            )}

            {/* One submission, in full - the request and response are the audit
                trail when the authority disputes something months later. */}
            <Modal
                open={!!detail}
                onClose={() => setDetail(null)}
                title={detail ? `${detail.Order_Number} · ${detail.Authority_Short_Name}` : ''}
                width="lg"
                footer={(
                    <>
                        <button type="button" className="btn-secondary" onClick={() => setDetail(null)}>
                            Close
                        </button>
                        {detail?.Queue_Status !== 'DONE' && (
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={() => { retry(detail.Fiscal_Queue_ID); setDetail(null); }}
                            >
                                <FiRefreshCw /> Try again
                            </button>
                        )}
                    </>
                )}
            >
                {detail && (
                    <div className="space-y-3 text-sm">
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                            <div>
                                <dt className="text-slate-500">Status</dt>
                                <dd><StatusBadge status={detail.Queue_Status} /></dd>
                            </div>
                            <div>
                                <dt className="text-slate-500">Attempts</dt>
                                <dd className="tabular">{detail.Attempt_Count} of {detail.Max_Attempts}</dd>
                            </div>
                            <div>
                                <dt className="text-slate-500">Invoice number</dt>
                                <dd className="tabular">{detail.Authority_Invoice_Number || '—'}</dd>
                            </div>
                            <div>
                                <dt className="text-slate-500">Environment</dt>
                                <dd>{detail.Is_Production ? 'Production' : 'Sandbox'}</dd>
                            </div>
                            <div>
                                <dt className="text-slate-500">Last attempt</dt>
                                <dd>{detail.Last_Attempt_Date_Time
                                    ? formatDateTime(detail.Last_Attempt_Date_Time) : '—'}</dd>
                            </div>
                            <div>
                                <dt className="text-slate-500">Next attempt</dt>
                                <dd>{detail.Next_Attempt_Date_Time
                                    ? formatDateTime(detail.Next_Attempt_Date_Time) : '—'}</dd>
                            </div>
                        </dl>

                        {detail.Last_Error && (
                            <div className="rounded-lg bg-red-50 p-3">
                                <p className="mb-1 font-medium text-red-800">What the gateway said</p>
                                <p className="text-red-700">{detail.Last_Error}</p>
                            </div>
                        )}

                        {detail.Response_Message && detail.Response_Message !== detail.Last_Error && (
                            <div className="rounded-lg bg-slate-50 p-3">
                                <p className="mb-1 font-medium text-slate-700">Response</p>
                                <p className="text-slate-600">{detail.Response_Message}</p>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default FiscalPage;
